import {Box} from '@mui/material';
import React, {useState} from 'react';

import CustomButton from '../../../components/CustomButton/CustomButton';
import CustomSelectField from '../../../components/CustomFormFields/CustomSelectField/CustomSelectField';
import CustomTextField from '../../../components/CustomFormFields/CustomTextField/CustomTextField';
import {addOrUpdateConfigMongo, getConfigMongo, refreshConfig} from '../../../service/admin';
import {handleError} from '../../../utils/errors';

const ConfigMongo = ({setSnackBarAlert, setIsLoading}) => {
  const [formData, setFormData] = useState({
    operationType: {
      type: 'select',
      label: 'Operation Type',
      value: 'GET',
      options: [
        {code: 'GET', label: 'Get'},
        {code: 'UPDATE', label: 'Update'},
        {code: 'ADD', label: 'Add'},
        {code: 'REFRESH', label: 'Refresh'},
      ],
      error: false,
      hide: false,
      helperText: '',
    },
    key: {
      type: 'text',
      label: 'Key',
      value: '',
      error: false,
      hide: false,
      helperText: '',
      minWidth: '58%',
    },
    valueType: {
      type: 'select',
      label: 'Value Type',
      value: 'string',
      options: [
        {code: 'string', label: 'String'},
        {code: 'json', label: 'JSON'},
        {code: 'number', label: 'Number'},
      ],
      error: false,
      hide: true,
      helperText: '',
    },
    value: {
      type: 'text',
      label: 'Value',
      value: '',
      disabled: true,
      error: false,
      hide: false,
      helperText: '',
      minHeight: 150,
      multiline: true,
      minWidth: '96%',
    },
  });

  const handleFormChange = (e) => {
    if (e.target.id === 'operationType') {
      setFormData((prevFormData) => ({
        ...prevFormData,
        valueType: {
          ...prevFormData.valueType,
          hide: e.target.value === 'UPDATE' || e.target.value === 'ADD' ? false : true,
          error: false,
          helperText: '',
        },
        value: {
          ...prevFormData.value,
          value: '',
          disabled: e.target.value === 'UPDATE' || e.target.value === 'ADD' ? false : true,
          error: false,
          hide: e.target.value === 'REFRESH' ? true : false,
          helperText: '',
        },
        key: {
          ...prevFormData.key,
          hide: e.target.value === 'REFRESH' ? true : false,
        },
      }));
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [e.target.id]: {
        ...prevFormData[e.target.id],
        value: e.target.value,
        error: false,
        helperText: '',
      },
    }));
  };

  const validateForm = () => {
    let isValid = true;
    switch (formData.operationType.value) {
      case 'GET':
        if (!formData.key.value) {
          isValid = false;
          setFormData((prevFormData) => ({
            ...prevFormData,
            key: {
              ...prevFormData.key,
              error: true,
              helperText: 'required',
            },
          }));
        }
        break;
      case 'UPDATE':
      case 'ADD':
        if (!formData.key.value) {
          isValid = false;
          setFormData((prevFormData) => ({
            ...prevFormData,
            key: {
              ...prevFormData.key,
              error: true,
              helperText: 'required',
            },
          }));
        } else if (formData.valueType.value === 'number' && !isFinite(formData.value.value)) {
          isValid = false;
          setFormData((prevFormData) => ({
            ...prevFormData,
            value: {
              ...prevFormData.value,
              error: true,
              helperText: 'Invalid number value',
            },
          }));
        } else if (formData.valueType.value === 'json') {
          try {
            JSON.parse(formData.value.value);
          } catch (error) {
            isValid = false;
            setFormData((prevFormData) => ({
              ...prevFormData,
              value: {
                ...prevFormData.value,
                error: true,
                helperText: 'Invalid JSON',
              },
            }));
          }
        }
        break;
      default:
    }
    return isValid;
  };

  const createDataForAddOrUpdate = () => {
    const payload = {
      operationType: formData.operationType.value,
      data: {
        key: formData.key.value,
        value:
          formData.valueType.value === 'number'
            ? +formData.value.value
            : formData.valueType.value === 'json'
              ? JSON.parse(formData.value.value)
              : formData.value.value,
      },
    };
    return payload;
  };

  const handleButtonClick = async (e) => {
    try {
      const isValid = validateForm();
      if (isValid) {
        setIsLoading(true);
        switch (formData.operationType.value) {
          case 'GET':
            {
              const result = await getConfigMongo({key: formData.key.value});
              if (!result) {
                setSnackBarAlert((prevState) => ({
                  ...prevState,
                  isOpen: true,
                  message: 'NOT FOUND',
                  severity: 'error',
                }));
              } else {
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  value: {
                    ...prevFormData.value,
                    value: JSON.stringify(result),
                  },
                }));
              }
            }
            break;
          case 'UPDATE':
          case 'ADD':
            await addOrUpdateConfigMongo(createDataForAddOrUpdate());
            setSnackBarAlert((prevState) => ({
              ...prevState,
              isOpen: true,
              message: 'saved',
              severity: 'success',
            }));
            break;
          case 'REFRESH':
            await refreshConfig('phoenix');
            setSnackBarAlert((prevState) => ({
              ...prevState,
              isOpen: true,
              message: 'Refresh Successful.',
              severity: 'success',
            }));
            break;
          default:
        }
      }
    } catch (error) {
      setSnackBarAlert((prevState) => ({
        ...prevState,
        isOpen: true,
        message: handleError(error),
        severity: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={{p: 1, display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
        {formData &&
          Object.keys(formData)?.map((key, index) => {
            if (!formData[key].hide && formData[key].type === 'text') {
              return (
                <CustomTextField
                  key={key}
                  label={formData[key].label}
                  id={key}
                  disabled={formData[key].disabled}
                  value={formData[key]?.value}
                  onChange={handleFormChange}
                  error={formData[key]?.error}
                  helperText={formData[key].helperText || ''}
                  multiline={formData[key]?.multiline || false}
                  minWidth={formData[key]?.minWidth}
                />
              );
            }
            if (!formData[key].hide && formData[key].type === 'select') {
              return (
                <CustomSelectField
                  key={key}
                  id={key}
                  label={formData[key].label}
                  disabled={formData[key].disabled}
                  options={formData[key].options}
                  value={formData[key]?.value}
                  onChange={handleFormChange}
                  error={formData[key]?.error}
                  helperText={formData[key].helperText || ''}
                />
              );
            }
            return null;
          })}
        <br />
      </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', padding: '2rem'}}>
        <CustomButton
          text={formData.operationType.options.find((option) => option.code === formData.operationType.value).label}
          onClick={handleButtonClick}
        />
      </Box>
    </>
  );
};

export default ConfigMongo;
